import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getAccessToken, getRefreshToken, saveAccessToken, saveRefreshToken, removeAccessToken } from '../utils/cookie.utils';
import { isWebBuild } from '../utils/environment';
import { getEncryptedToken } from './interceptors/jwt.interceptor';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Callback types for handling auth and wallet events
type LogoutCallback = () => void;
type WalletUpdateCallback = (wallets: { [key: string]: number }[]) => void;
type NavigateCallback = (route: string) => void;

let logoutCallback: LogoutCallback | null = null;
let walletUpdateCallback: WalletUpdateCallback | null = null;
let navigateCallback: NavigateCallback | null = null;

/**
 * Register callbacks for handling auth and navigation events
 * This allows the core package to communicate with the UI layer
 */
export function registerAuthCallbacks(
    onLogout: LogoutCallback,
    onWalletUpdate: WalletUpdateCallback,
    onNavigate: NavigateCallback
) {
    logoutCallback = onLogout;
    walletUpdateCallback = onWalletUpdate;
    navigateCallback = onNavigate;
}

// Request interceptor
apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        const isWeb = isWebBuild();

        // Get access token
        let accessToken = typeof window !== 'undefined' ? (window as any)['appAccessToken'] : null;
        if (isWeb && !accessToken) {
            accessToken = getAccessToken();
        }

        // Add JWT in Authorization Bearer header
        if (accessToken && config.headers) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        // Add app key if available (skip for Google APIs)
        const encrypted = getEncryptedToken();
        if (encrypted && config.url && !config.url.includes('googleapis') && config.headers) {
            config.headers['app-key'] = `${encrypted}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Dedupe: only one refresh in flight
let refreshPromise: Promise<boolean> | null = null;

function doLogoutClear(): void {
    if (typeof window !== 'undefined' && (window as any)['appAccessToken']) {
        delete (window as any)['appAccessToken'];
    }
    removeAccessToken();
    if (isWebBuild() && typeof window !== 'undefined' && window.localStorage) {
        try {
            localStorage.clear();
            console.log('🧹 401 Handler: Cleared auth storage');
        } catch (e) {
            console.warn('Could not clear localStorage:', e);
        }
    }
    if (logoutCallback) logoutCallback();
    if (!isWebBuild() && typeof parent !== 'undefined') {
        try {
            parent.postMessage({ pluginMessage: { type: 'logout' } }, '*');
        } catch (e) {
            console.warn('Could not notify plugin of logout:', e);
        }
    }
}

// Response interceptor
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error: AxiosError) => {
        // 401: try refresh first, then logout if refresh fails or no refresh_token
        if (error.response && error.response.status === 401 && error.config) {
            const url = (error.config.url || '');
            const isRefreshEndpoint = url.includes('auth/refresh');

            if (isRefreshEndpoint) {
                console.warn('401 on refresh endpoint - logging out');
                doLogoutClear();
                return Promise.reject(error);
            }

            const attemptRefresh = async (): Promise<boolean> => {
                const rt = getRefreshToken();
                if (!rt) return false;
                try {
                    const { authService } = await import('./services/auth.service');
                    const r = await authService.refreshToken(rt);
                    saveAccessToken(r.access_token);
                    saveRefreshToken(r.refresh_token);
                    if (typeof window !== 'undefined') (window as any)['appAccessToken'] = r.access_token;
                    return true;
                } catch (e) {
                    console.warn('Token refresh failed:', e);
                    return false;
                } finally {
                    refreshPromise = null;
                }
            };

            if (!refreshPromise) refreshPromise = attemptRefresh();
            const ok = await refreshPromise;
            if (ok) {
                return apiClient.request(error.config);
            }

            console.warn('401 Unauthorized - refresh failed or no refresh token, logging out', {
                url: error.config?.url,
                method: error.config?.method,
            });
            doLogoutClear();
        }

    
        // Always reject the promise to maintain the error flow
        return Promise.reject(error);
    }
);

export default apiClient;

